import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ItemFollow, type AssignmentKind } from "@heraion/shared";

import type { ProgramApiFetch } from "../ProgramEditor/types";
import { writeError, type DurableWriteFn } from "../seams";
import { favoriteKey } from "./useFavorites";

// What the caller FOLLOWS (next/coaching.md, "La SPEC del follower", phase
// F3). A follow is a FOLLOW#<kind>#<itemId> pointer row in the caller's own
// partition to an item that lives in its AUTHOR's: the caller reads it and
// never writes it. Server contract: GET /api/me/follows + DELETE
// /api/me/follows/:kind/:itemId (apps/api/src/routes/follows.ts).
//
// Same shape as useFavorites on purpose: one GET at mount, a Set of
// `${kind}/${itemId}` keys, an optimistic write via the host seam, revert on
// a server verdict, hand the error back so the host renders the message.
//
// ⛔ No POST, and that asymmetry with favorites is the model: a star is a
// preference of yours, a follow is a READ PERMISSION on someone else's
// partition, and the only thing that grants it is an accepted assignment
// proposal. The route has no POST either.
//
// ⛔ Nothing to do with the card's onFollow/onUnfollow, which move a program
// of your own along the Follow+Publish status axis (shared/programsList.ts).

/** Identity of a followed item. Delegates to `favoriteKey` because
 *  FAVORITE# and FOLLOW# are the same `${kind}/${itemId}` identity, and two
 *  derivations of it could drift. */
export const followKey = (kind: AssignmentKind, itemId: string): string =>
  favoriteKey(kind, itemId);

export type FollowsStatus = "loading" | "loaded" | "error";

export function useFollows(opts: {
  apiFetch: ProgramApiFetch;
  /** Host write seam: mobile queues when offline, web sends only. */
  durableWrite: DurableWriteFn;
}) {
  const [keys, setKeys] = useState<ReadonlySet<string>>(() => new Set());
  const [status, setStatus] = useState<FollowsStatus>("loading");
  // Key with a write in flight. Double-tap guard, same contract as
  // useFavorites' pendingKey.
  const [pendingKey, setPendingKey] = useState<string | null>(null);

  const apiRef = useRef(opts.apiFetch);
  const writeRef = useRef(opts.durableWrite);
  useEffect(() => {
    apiRef.current = opts.apiFetch;
    writeRef.current = opts.durableWrite;
  });

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const raw = await apiRef.current<unknown>("/api/me/follows");
        if (cancelled) return;
        const arr =
          raw && typeof raw === "object" && "items" in raw
            ? (raw as { items: unknown }).items
            : [];
        const next = new Set<string>();
        if (Array.isArray(arr)) {
          for (const item of arr) {
            const parsed = ItemFollow.safeParse(item);
            if (parsed.success)
              next.add(followKey(parsed.data.kind, parsed.data.itemId));
          }
        }
        setKeys(next);
        setStatus("loaded");
      } catch {
        if (!cancelled) setStatus("error");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  /** Followed PROGRAM ids, for splitProgramsByStatus: a followed program is
   *  bucketed by the follow, never by the author's status. */
  const followedProgramIds = useMemo<ReadonlySet<string>>(() => {
    const out = new Set<string>();
    for (const k of keys) {
      const [kind, ...rest] = k.split("/");
      if (kind === "program" && rest.length > 0) out.add(rest.join("/"));
    }
    return out;
  }, [keys]);

  const isFollowed = useCallback(
    (kind: AssignmentKind, itemId: string): boolean =>
      keys.has(followKey(kind, itemId)),
    [keys],
  );

  /** Would unfollow send its DELETE, read from this render? Not with a
   *  write in flight, not for an item not followed. */
  const sendsUnfollow = useCallback(
    (kind: AssignmentKind, itemId: string): boolean =>
      pendingKey === null && keys.has(followKey(kind, itemId)),
    [keys, pendingKey],
  );

  /** Stop following. Optimistic: drop the key, fire the DELETE, put it back
   *  only on a server verdict. Resolves null on success, the Error on
   *  failure (state already reverted). Null too when nothing is sent
   *  (sendsUnfollow). */
  const unfollow = useCallback(
    async (kind: AssignmentKind, itemId: string): Promise<Error | null> => {
      const key = followKey(kind, itemId);
      if (!sendsUnfollow(kind, itemId)) return null;
      setPendingKey(key);
      setKeys((prev) => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
      try {
        const result = await writeRef.current({
          method: "DELETE",
          path: `/api/me/follows/${encodeURIComponent(kind)}/${encodeURIComponent(itemId)}`,
        });
        // A queued unfollow KEEPS the flip and reports success: it is durable
        // on mobile. Only a server verdict reverts.
        const err = writeError(result);
        if (err !== null) {
          setKeys((prev) => new Set(prev).add(key));
        }
        return err;
      } finally {
        setPendingKey(null);
      }
    },
    [sendsUnfollow],
  );

  return {
    followedKeys: keys,
    followedProgramIds,
    followsStatus: status,
    followPendingKey: pendingKey,
    isFollowed,
    sendsUnfollow,
    unfollow,
  };
}
